import type {ComponentPropsWithoutRef, ElementType, ReactNode} from 'react';
import {FiExternalLink} from 'react-icons/fi';
import {cn} from '../../../../../../lib/classname';

type Props<E extends ElementType> = ComponentPropsWithoutRef<E> & {
  as?: E;
  active?: boolean;
  isExternal?: boolean;
};
type ButtonType = 'button';

export const Button = <E extends ElementType = ButtonType>(
  props: Props<E>,
): ReactNode => {
  const {
    as = 'button',
    children,
    className,
    active,
    isExternal,
    ...rest
  } = props;
  const Component: ElementType = as ?? ('button' as ElementType);
  return (
    <Component
      className={cn(
        'inline-flex items-center rounded-[24px] px-16 py-[10px] bg-background text-primary',
        'font-bold text-[14px] whitespace-nowrap',
        'hover:opacity-60 focus-visible:border border-primary',
        active && 'bg-primary text-white border-white',
        className,
      )}
      {...(Component === 'button' && {type: 'button'})}
      {...rest}
    >
      {children}
      {isExternal && (
        <FiExternalLink className="ml-4 inline-block align-middle" size={16} />
      )}
    </Component>
  );
};

export const OutlineButton = <E extends ElementType = ButtonType>(
  props: Props<E>,
): ReactNode => {
  const {
    as = 'button',
    children,
    className,
    active,
    isExternal,
    ...rest
  } = props;
  const Component: ElementType = as ?? ('button' as ElementType);
  return (
    <Component
      className={cn(
        'inline-flex items-center rounded-[24px] px-12 py-[6px] text-[12px]',
        'pc:rounded-[28px] pc:px-16 pc:py-[10px] pc:text-[14px]',
        'bg-white font-bold text-primary whitespace-nowrap',
        'border-2 border-primary',
        'hover:opacity-60 focus-visible:bg-primary focus-visible:text-white',
        active &&
          'bg-primary text-white focus-visible:bg-white focus-visible:text-primary',
        className,
      )}
      {...(as === 'button' && {type: 'button'})}
      {...rest}
    >
      {children}
      {isExternal && (
        <FiExternalLink className="ml-4 inline-block align-middle" size={16} />
      )}
    </Component>
  );
};
